import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';

export type StorageKey = string | string & {};

export function useSessionStorage<T>(key: StorageKey, defaultValue: T) {
  return useStorage<T>(key, defaultValue, window.sessionStorage);
}

export function useLocalStorage<T>(key: StorageKey, defaultValue: T) {
  return useStorage<T>(key, defaultValue, window.localStorage);
}

export default function useStorage<T>(key: StorageKey, defaultValue: T, storageObject: Storage = window.localStorage) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const json = storageObject.getItem(key);
      if (json != null) return JSON.parse(json);
      return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
    }
    catch (e) {
      console.log('error in parse key ' + key, e);
      return defaultValue;
    }
  });

  useEffect(() => {
    if (storedValue === undefined) return storageObject.removeItem(key);
    storageObject.setItem(key, JSON.stringify(storedValue));
  }, [key, storedValue, storageObject]);

  const remove = useCallback(() => {
    setStoredValue(undefined as any);
  }, []);

  const setValue = useCallback((value: any) => {
    try {
      const newValue = typeof value === 'function' ? value(storedValue) : value;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(newValue));
      }
      setStoredValue(newValue);
    }
    catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [storedValue, setValue, remove] as [T, Dispatch<SetStateAction<T>>, () => void];
}
